"use client";

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getExpandedRowModel,
  ExpandedState,
} from "@tanstack/react-table";
import { t } from "i18next";

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../Table/Table";
import { ScrollArea } from "../ScrollArea/ScrollArea";
import React, { useEffect } from "react";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  compact?: boolean;
  onClickRow?: Function;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  compact = false,
  onClickRow,
}: DataTableProps<TData, TValue>) {
  const [expanded, setExpanded] = React.useState<ExpandedState>({});

  const table = useReactTable({
    data,
    columns,
    state: {
      expanded,
    },
    onExpandedChange: setExpanded,
    //@ts-ignore
    getSubRows: (row) => row.subRows, // Выбор свойства объекта, где находятся дочерние элементы дерева
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  useEffect(() => {
    table.toggleAllRowsExpanded();
  }, []);

  return (
    <ScrollArea>
      <div className={`${compact ? "max-w-[300px] w-[300px] border-l h-full" : "w-full"}`}>
        <Table>
          {!compact && (
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
          )}
          <TableBody className={`${compact ? "overflow-x" : ""}`}>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  className={"cursor-pointer"}
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  onClick={() => {
                    //@ts-ignore
                    onClickRow?.(row.original.id); // Передача id ряда для переданной функции
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={`${
                        compact ? "max-w-[300px] w-[300px] overflow-hidden min-h-12 h-12 " : "min-h-12 h-12"
                      }`}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  {t("no_data")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </ScrollArea>
  );
}
